import {
  Children,
  cloneElement,
  CSSProperties,
  isValidElement,
  memo,
  useCallback,
  useEffect,
  useRef,
  useState
} from 'react';
import { createPortal } from 'react-dom';

export type tooltipPositions =
  | 'top'
  | 'top-start'
  | 'top-end'
  | 'bottom'
  | 'bottom-start'
  | 'bottom-end'
  | 'left'
  | 'right';
interface TooltipProps {
  children: React.ReactNode;
  isActive?: boolean;
  text?: React.ReactNode | string;
  position?: tooltipPositions;
}

const Tooltip = ({ children, isActive = true, text = '', position = 'bottom' }: TooltipProps) => {
  const [isVisible, setIsVisible] = useState(false);
  const [styles, setStyles] = useState<CSSProperties | undefined>();

  const trigger = useRef<HTMLElement>();

  const getStyles = useCallback(
    () =>
      setStyles(() => {
        if (trigger.current) {
          const parentScrollTop = trigger.current.parentElement?.scrollTop || 0;
          const parentScrollLeft = trigger.current.parentElement?.scrollTop || 0;
          const rect = trigger.current.getBoundingClientRect();

          const transform = [
            ['top', 'bottom'].includes(position) && 'translateX(-50%)',
            (position === 'left' || position.includes('end')) && 'translateX(-100%)',
            ['left', 'right'].includes(position) && 'translateY(-50%)',
            position.includes('top') && 'translateY(-100%)'
          ]
            .filter((d) => d)
            .join(' ');

          switch (position) {
            case 'top':
              return {
                left: rect.left - parentScrollLeft + rect.width / 2,
                top: rect.top - parentScrollTop - 8,
                transform
              };

            case 'top-start':
              return {
                left: rect.left - parentScrollLeft,
                top: rect.top - parentScrollTop - 8,
                transform
              };

            case 'top-end':
              return {
                left: rect.left - parentScrollLeft + rect.width,
                top: rect.top - parentScrollTop - 8,
                transform
              };

            case 'right':
              return {
                left: rect.left - parentScrollLeft + rect.width + 8,
                top: rect.top - parentScrollTop + rect.height / 2,
                transform
              };

            case 'left':
              return {
                left: rect.left - parentScrollLeft - 8,
                top: rect.top - parentScrollTop + rect.height / 2,
                transform
              };

            case 'bottom-start':
              return {
                left: rect.left - parentScrollLeft,
                top: rect.top - parentScrollTop + rect.height + 8,
                transform
              };

            case 'bottom-end':
              return {
                left: rect.left - parentScrollLeft + rect.width,
                top: rect.top - parentScrollTop + rect.height + 8,
                transform
              };

            case 'bottom':
            default:
              return {
                left: rect.left - parentScrollLeft + rect.width / 2,
                top: rect.top - parentScrollTop + rect.height + 8,
                transform
              };
          }
        }
        return;
      }),
    [position]
  );

  useEffect(() => {
    if (trigger.current) {
      const element = trigger.current;
      const handleMouseEnter = () => {
        getStyles();
        setIsVisible(true);
      };
      const handleMouseLeave = () => setIsVisible(false);

      element.addEventListener('mouseenter', handleMouseEnter);
      element.addEventListener('mouseleave', handleMouseLeave);
      return () => {
        element.removeEventListener('mouseenter', handleMouseEnter);
        element.removeEventListener('mouseleave', handleMouseLeave);
      };
    }
  }, [getStyles]);

  if (!isActive) return children;

  if (!children || !isValidElement(children) || Children.count(children) !== 1) {
    throw new Error('Must have single child');
  }

  return (
    <>
      {isVisible &&
        createPortal(
          <div className="fixed z-50" style={{ ...styles }}>
            <div className="w-full max-w-[300px] min-w-[50px] rounded-lg bg-zinc-700/90 p-2 text-center text-xs whitespace-nowrap text-white">
              {text}
            </div>
          </div>,
          document.body
        )}
      {cloneElement(children, { ...children.props, ref: trigger })}
    </>
  );
};

export default memo(Tooltip);
